import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircleIcon, InfoIcon, ShieldCheckIcon, StethoscopeIcon } from 'lucide-react';
import { TextField } from '../components/Field';
import { Turnstile } from '../components/Turnstile';
import { usePortal, toResearcher } from '../contexts/PortalContext';
import { homeFor } from '../data/roles';
import * as api from '../api/research';
import { INSTITUTION, IRB_EMAIL } from '../data/proposalContent';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+\-\s()]{7,20}$/;

interface FormState {
  fullName: string;
  designation: string;
  institution: string;
  email: string;
  contact: string;
  password: string;
  confirm: string;
}

const initial: FormState = {
  fullName: '',
  designation: '',
  institution: INSTITUTION,
  email: '',
  contact: '',
  password: '',
  confirm: ''
};

export function SignUp() {
  const { adoptSession, config } = usePortal();
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [stage, setStage] = useState<'form' | 'confirm'>('form');
  const [pendingToken, setPendingToken] = useState('');
  const [pin, setPin] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [captcha, setCaptcha] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [captchaBroken, setCaptchaBroken] = useState(false);
  const siteKey = config?.turnstileSiteKey ?? '';
  const irbEmail = config?.irbEmail ?? IRB_EMAIL;

  const set = (key: keyof FormState) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!form.fullName.trim()) next.fullName = 'Enter your full name.';
    if (!form.designation.trim()) next.designation = 'Enter your designation.';
    if (!form.institution.trim()) next.institution = 'Enter your institution.';
    if (!EMAIL_RE.test(form.email.trim())) next.email = 'Enter a valid email address.';
    if (!PHONE_RE.test(form.contact.trim())) next.contact = 'Enter a valid contact number.';
    if (form.password.length < 8) next.password = 'Password must be at least 8 characters.';
    if (form.confirm !== form.password) next.confirm = 'Passwords do not match.';
    setErrors(next);
    setFailure(null);
    if (Object.keys(next).length > 0) return;

    if (siteKey && !captcha && !captchaBroken) {
      setFailure('Please complete the security check.');
      return;
    }

    setBusy(true);
    try {
      const res = await api.register({
        FullName: form.fullName.trim(),
        Email: form.email.trim(),
        Contact: form.contact.trim(),
        Password: form.password,
        Designation: form.designation.trim(),
        Institution: form.institution.trim(),
        TurnstileToken: captcha
      });

      if (res.ok && res.pendingToken) {
        setPendingToken(res.pendingToken);
        setNotice(res.message);
        setStage('confirm');
      } else {
        setFailure(res.message || 'Could not create the account.');
      }
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not create the account.');
    } finally {
      setBusy(false);
      setCaptchaNonce((n) => n + 1);
    }
  };

  const handleConfirm = async (event: React.FormEvent) => {
    event.preventDefault();
    setFailure(null);
    if (pin.trim().length < 4) {
      setErrors({ pin: 'Enter the 6-digit code from your email.' });
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const res = await api.verifyPin({ PendingToken: pendingToken, Pin: pin.trim() });
      if (res.ok && res.sessionToken && res.profile) {
        adoptSession(res.sessionToken, toResearcher(res.profile));
        navigate(homeFor(res.profile), { replace: true });
        return;
      }
      setFailure(res.message || 'That code was not accepted.');
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'That code was not accepted.');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setFailure(null);
    if (siteKey && !captcha && !captchaBroken) {
      setFailure('Please complete the security check.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.resendPin({ PendingToken: pendingToken, TurnstileToken: captcha });
      if (res.ok && res.pendingToken) setPendingToken(res.pendingToken);
      setNotice(res.message);
      if (!res.ok) setFailure(res.message);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not resend the code.');
    } finally {
      setBusy(false);
      setCaptchaNonce((n) => n + 1);
    }
  };

  return (
    <div className="auth">
      <aside className="auth-aside">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <StethoscopeIcon size={20} />
          </span>
          <span>
            <span className="brand-name">{INSTITUTION}</span>
            <span className="brand-sub">Institutional Review Board</span>
          </span>
        </div>

        <div>
          <h1 className="auth-headline">Register as a researcher</h1>
          <p className="auth-lede">
            One account lets you draft, save and track every proposal you submit to the
            Research Advisory &amp; Grant Review Committee. Details you enter here
            pre-fill the Principal Investigator section of your submission.
          </p>
        </div>

        <p className="auth-foot">
          Registration issues? Write to{' '}
          <a href={`mailto:${irbEmail}`} style={{ color: 'var(--blue-200)' }}>
            {irbEmail}
          </a>
        </p>
      </aside>

      <main className="auth-main">
        <div className="auth-card" style={{ maxWidth: 560 }}>
          {stage === 'form' ? (
            <>
              <h2 className="auth-title">Create your account</h2>
              <p className="auth-sub">All fields are required for IRB verification.</p>
            </>
          ) : (
            <>
              <h2 className="auth-title">Confirm your email</h2>
              <p className="auth-sub">
                Enter the 6-digit code we sent to {form.email.trim()}.
              </p>
            </>
          )}

          {notice ? (
            <div className="notice" style={{ marginTop: 'var(--space-4)' }} role="status">
              <InfoIcon size={18} aria-hidden="true" />
              <span>{notice}</span>
            </div>
          ) : null}

          {failure ? (
            <div
              className="notice notice--error"
              style={{ marginTop: 'var(--space-4)' }}
              role="alert"
            >
              <AlertCircleIcon size={18} aria-hidden="true" />
              <span>{failure}</span>
            </div>
          ) : null}

          {stage === 'form' ? (
            <form className="auth-form" onSubmit={handleSubmit} noValidate>
              <TextField
                id="su-name"
                label="Full name"
                required
                autoComplete="name"
                value={form.fullName}
                onChange={set('fullName')}
                error={errors.fullName}
              />

              <div className="grid-2" style={{ marginTop: 'var(--space-4)' }}>
                <TextField
                  id="su-designation"
                  label="Designation"
                  required
                  placeholder="e.g. Assistant Professor"
                  value={form.designation}
                  onChange={set('designation')}
                  error={errors.designation}
                />
                <TextField
                  id="su-institution"
                  label="Institution"
                  required
                  value={form.institution}
                  onChange={set('institution')}
                  error={errors.institution}
                />
              </div>

              <div className="grid-2" style={{ marginTop: 'var(--space-4)' }}>
                <TextField
                  id="su-email"
                  label="Email address"
                  required
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={set('email')}
                  error={errors.email}
                />
                <TextField
                  id="su-contact"
                  label="Contact no."
                  required
                  type="tel"
                  placeholder="03000000000"
                  value={form.contact}
                  onChange={set('contact')}
                  error={errors.contact}
                />
              </div>

              <div className="grid-2" style={{ marginTop: 'var(--space-4)' }}>
                <TextField
                  id="su-password"
                  label="Password"
                  required
                  type="password"
                  autoComplete="new-password"
                  hint="Minimum 8 characters."
                  value={form.password}
                  onChange={set('password')}
                  error={errors.password}
                />
                <TextField
                  id="su-confirm"
                  label="Confirm password"
                  required
                  type="password"
                  autoComplete="new-password"
                  value={form.confirm}
                  onChange={set('confirm')}
                  error={errors.confirm}
                />
              </div>

              <div style={{ marginTop: 'var(--space-5)' }}>
                <Turnstile
                  siteKey={siteKey}
                  action="register"
                  resetSignal={captchaNonce}
                  onToken={setCaptcha}
                  onUnavailable={setCaptchaBroken}
                />
              </div>

              <div className="notice" style={{ marginTop: 'var(--space-5)' }}>
                <ShieldCheckIcon size={18} aria-hidden="true" />
                <span>
                  Your account details are used only for correspondence about your
                  proposals and grant review.
                </span>
              </div>

              <button
                type="submit"
                className="btn btn--primary btn--block"
                style={{ marginTop: 'var(--space-5)' }}
                disabled={busy}
              >
                {busy ? 'Creating account…' : 'Create account'}
              </button>
            </form>
          ) : (
            <form className="auth-form" onSubmit={handleConfirm} noValidate>
              <TextField
                id="su-pin"
                label="Confirmation code"
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                value={pin}
                onChange={setPin}
                error={errors.pin}
              />

              <Turnstile
                siteKey={siteKey}
                action="resend"
                resetSignal={captchaNonce}
                onToken={setCaptcha}
                onUnavailable={setCaptchaBroken}
              />

              <button
                type="submit"
                className="btn btn--primary btn--block"
                style={{ marginTop: 'var(--space-5)' }}
                disabled={busy}
              >
                {busy ? 'Checking…' : 'Confirm and continue'}
              </button>

              <div className="auth-row-between" style={{ marginTop: 'var(--space-4)' }}>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setStage('form');
                    setFailure(null);
                    setNotice(null);
                  }}
                >
                  Change my details
                </button>
                <button type="button" className="link-button" onClick={handleResend} disabled={busy}>
                  Send a new code
                </button>
              </div>
            </form>
          )}

          <p className="auth-alt">
            Already registered? <Link to="/">Sign in</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
